import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Search, Package, RefreshCw, Save, Trash2, Maximize2, X } from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import { useApp } from '../App';

export default function ProductVisualizer() {
  // Datos compartidos desde el contexto global (carga instantanea si ya estan precargados)
  const { siigoProducts, siigoProductsLoading, loadSiigoProducts, inventoryLocalData } = useApp();

  // Estados para productos
  const [currentIndex, setCurrentIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingImages, setSavingImages] = useState(false);

  // Estados para imágenes
  const [images, setImages] = useState([null, null, null]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(false);

  // Cache en memoria de imagenes por product_id (para navegacion instantanea)
  const imageCacheRef = useRef(new Map());
  const inFlightRef = useRef(new Set());

  // Función para extraer información de cantidad por caja del nombre
  const extraerCantidadPorCaja = (nombre) => {
    if (!nombre) return null;
    const regex = /\(C:\s*([\d.]+)M\s*X\s*([\d.]+)U\)/i;
    const match = nombre.match(regex);
    if (match) {
      return {
        metrosCuadrados: parseFloat(match[1]),
        unidades: parseFloat(match[2])
      };
    }
    return null;
  };

  // Función para limpiar el nombre del producto
  const limpiarNombre = (nombre) => {
    if (!nombre) return 'Sin nombre';
    let nombreLimpio = nombre;
    nombreLimpio = nombreLimpio.replace(/\s*\(C:\s*[\d.]+M\s*X\s*[\d.]+U\)/gi, '');
    nombreLimpio = nombreLimpio.replace(/\s*(1RA|2DA)\s*/gi, ' ');
    nombreLimpio = nombreLimpio.replace(/\s+/g, ' ').trim();
    return nombreLimpio || 'Sin nombre';
  };

  // Cargar imágenes de un producto desde el backend (con cache en memoria)
  const cargarImagenesProducto = useCallback(async (productId) => {
    if (!productId) return [null, null, null];
    const cacheKey = String(productId);
    const cached = imageCacheRef.current.get(cacheKey);
    if (cached) return cached;
    if (inFlightRef.current.has(cacheKey)) return null;
    inFlightRef.current.add(cacheKey);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product-images/${productId}`, {
        cache: 'default'
      });
      const data = await response.json();
      const imgs = (data && data.success && data.images) ? data.images : [null, null, null];
      imageCacheRef.current.set(cacheKey, imgs);
      return imgs;
    } catch (error) {
      console.error('Error cargando imágenes:', error);
      return [null, null, null];
    } finally {
      inFlightRef.current.delete(cacheKey);
    }
  }, []);

  // Prefetch en batch (para cargar imagenes de productos adyacentes en 1 sola llamada)
  const prefetchImagenesBatch = useCallback(async (ids) => {
    const idsToFetch = ids
      .filter(Boolean)
      .map(String)
      .filter(id => !imageCacheRef.current.has(id) && !inFlightRef.current.has(id));
    if (idsToFetch.length === 0) return;
    idsToFetch.forEach(id => inFlightRef.current.add(id));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product-images/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: idsToFetch })
      });
      const data = await response.json();
      if (data && data.success && data.images) {
        Object.entries(data.images).forEach(([pid, imgs]) => {
          imageCacheRef.current.set(String(pid), imgs || [null, null, null]);
        });
      }
    } catch (error) {
      console.warn('Prefetch imagenes batch falló:', error);
    } finally {
      idsToFetch.forEach(id => inFlightRef.current.delete(id));
    }
  }, []);

  // Guardar imágenes de un producto en el backend
  const guardarImagenesProducto = async (productId, imagesToSave) => {
    if (!productId) return;
    setSavingImages(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product-images/${productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, images: imagesToSave })
      });
      const data = await response.json();
      if (data.success) {
        console.log('✅ Imágenes guardadas correctamente');
      }
    } catch (error) {
      console.error('Error guardando imágenes:', error);
    } finally {
      setSavingImages(false);
    }
  };

  // Transformacion memoizada de productos Siigo (solo activos) con datos locales
  const productos = useMemo(() => {
    if (!siigoProducts || siigoProducts.length === 0) return [];
    const datosLocales = inventoryLocalData || {};
    const activos = siigoProducts.filter(p => p.active === true);
    return activos.map(p => {
      const priceList = p.prices?.[0]?.price_list || [];
      const efectivo = priceList.find(pl => pl.position === 1)?.value || 0;
      const tarjeta = priceList.find(pl => pl.position === 2)?.value || 0;
      const datosProducto = datosLocales[p.id] || datosLocales[p.code] || { costo: 0, stock_minimo: 0 };
      let stockTotal = 0;
      if (p.warehouses && p.warehouses.length > 0) {
        stockTotal = p.warehouses.reduce((sum, w) => sum + (w.quantity || 0), 0);
      } else {
        stockTotal = p.available_quantity || 0;
      }
      return {
        id: p.id,
        codigo: p.code || '',
        nombre: p.name || '',
        categoria: p.account_group?.name || 'Sin categoría',
        cantidad: stockTotal,
        stock_minimo: datosProducto.stock_minimo || 0,
        costo: datosProducto.costo || 0,
        precio_lista1: efectivo,
        precio_lista2: tarjeta,
        referencia: p.reference || '',
        warehouses: p.warehouses || [],
        activo: p.active
      };
    });
  }, [siigoProducts, inventoryLocalData]);

  const productosFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return productos;
    const termino = searchTerm.toLowerCase().trim();
    return productos.filter(p =>
      p.nombre?.toLowerCase().includes(termino) ||
      p.referencia?.toLowerCase().includes(termino) ||
      p.codigo?.toLowerCase().includes(termino)
    );
  }, [productos, searchTerm]);

  // Resetear index cuando cambia el filtro
  useEffect(() => { setCurrentIndex(0); }, [searchTerm]);

  // Disparar carga si el contexto aun no tiene productos
  useEffect(() => {
    if ((!siigoProducts || siigoProducts.length === 0) && !siigoProductsLoading) {
      loadSiigoProducts();
    }
  }, [siigoProducts, siigoProductsLoading, loadSiigoProducts]);

  // Boton de recargar (fuerza refresh desde Siigo)
  const cargarProductos = useCallback(async () => {
    imageCacheRef.current.clear();
    await loadSiigoProducts(true);
  }, [loadSiigoProducts]);

  // Producto actual
  const productoActual = productosFiltrados[currentIndex] || null;
  const cantidadPorCaja = productoActual ? extraerCantidadPorCaja(productoActual.nombre) : null;

  // Cargar imágenes cuando cambia el producto (instantaneo si esta en cache)
  useEffect(() => {
    if (!productoActual?.id) return;
    const pid = String(productoActual.id);
    const cached = imageCacheRef.current.get(pid);
    if (cached) {
      setImages(cached);
      setImagesLoaded(true);
    } else {
      setImagesLoaded(false);
      setImages([null, null, null]);
      cargarImagenesProducto(productoActual.id).then(imgs => {
        if (imgs) {
          setImages(imgs);
          setImagesLoaded(true);
        }
      });
    }

    // Prefetch en background: 2 productos anteriores y 2 siguientes (navegacion fluida)
    const adyacentes = [];
    for (let off = -2; off <= 2; off++) {
      if (off === 0) continue;
      const idx = currentIndex + off;
      if (idx >= 0 && idx < productosFiltrados.length) {
        const p = productosFiltrados[idx];
        if (p?.id) adyacentes.push(p.id);
      }
    }
    if (adyacentes.length > 0) {
      // Delay pequeno para no competir con la imagen actual
      const t = setTimeout(() => prefetchImagenesBatch(adyacentes), 50);
      return () => clearTimeout(t);
    }
  }, [productoActual?.id, currentIndex, productosFiltrados, cargarImagenesProducto, prefetchImagenesBatch]);

  // Navegación
  const irAnterior = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setCurrentImageIndex(0);
    }
  };

  const irSiguiente = () => {
    if (currentIndex < productosFiltrados.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentImageIndex(0);
    }
  };

  // Teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') irAnterior();
      if (e.key === 'ArrowRight') irSiguiente();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, productosFiltrados.length]);

  // Placeholder SVG
  const placeholderSVG = (
    <svg className="w-full h-full" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="400" fill="#f1f5f9"/>
      <g opacity="0.4">
        <circle cx="200" cy="180" r="40" stroke="#94a3b8" strokeWidth="4" fill="none"/>
        <rect x="180" y="160" width="40" height="40" fill="#94a3b8"/>
        <path d="M200 160 L200 200 M180 180 L220 180" stroke="#f1f5f9" strokeWidth="4" strokeLinecap="round"/>
      </g>
      <text x="200" y="260" fontFamily="Arial, sans-serif" fontSize="18" fill="#64748b" textAnchor="middle">
        Haz clic para agregar imagen
      </text>
    </svg>
  );

  // Manejar cambio de imagen
  const handleFileChange = (index, event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es muy grande. Máximo 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        const newImages = [...images];
        newImages[index] = e.target.result;
        setImages(newImages);
        // Actualizar cache inmediatamente para que no se pierda al navegar
        if (productoActual?.id) {
          imageCacheRef.current.set(String(productoActual.id), newImages);
          await guardarImagenesProducto(productoActual.id, newImages);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = (index) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => handleFileChange(index, e);
    input.click();
  };

  const changeImage = (index) => {
    if (images[index] === null) {
      triggerFileInput(index);
      return;
    }
    setCurrentImageIndex(index);
  };

  // Eliminar imagen
  const eliminarImagen = async (index) => {
    const newImages = [...images];
    newImages[index] = null;
    setImages(newImages);

    if (productoActual?.id) {
      imageCacheRef.current.set(String(productoActual.id), newImages);
      await guardarImagenesProducto(productoActual.id, newImages);
    }
  };

  if (siigoProductsLoading && productos.length === 0) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-slate-600">Cargando productos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gradient-to-br from-slate-50 to-slate-100 overflow-auto">
      {/* Barra de búsqueda */}
      <div className="sticky top-0 z-10 bg-white shadow-md p-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nombre, referencia o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all text-lg"
            />
          </div>
          <button
            onClick={cargarProductos}
            className="p-3 bg-purple-100 text-purple-600 rounded-xl hover:bg-purple-200 transition-colors"
            title="Recargar productos"
          >
            <RefreshCw size={24} />
          </button>
          {savingImages && (
            <div className="flex items-center gap-2 text-green-600">
              <Save size={20} className="animate-pulse" />
              <span className="text-sm">Guardando...</span>
            </div>
          )}
          <div className="text-sm text-gray-500 whitespace-nowrap">
            {productosFiltrados.length} producto{productosFiltrados.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      {productosFiltrados.length === 0 ? (
        <div className="flex items-center justify-center h-[calc(100%-80px)]">
          <div className="text-center">
            <Package className="w-24 h-24 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500">
              {searchTerm ? 'No se encontraron productos' : 'No hay productos en el inventario'}
            </p>
          </div>
        </div>
      ) : (
        <div className="h-[calc(100%-80px)] w-full p-4 flex items-center">
          {/* Flecha izquierda */}
          <button
            onClick={irAnterior}
            disabled={currentIndex === 0}
            className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-all mr-4 ${
              currentIndex === 0
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                : 'bg-white shadow-lg text-purple-600 hover:bg-purple-50 hover:scale-110'
            }`}
          >
            <ChevronLeft size={32} />
          </button>

          {/* Grid principal */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 h-full max-w-6xl mx-auto">
            
            {/* COLUMNA IZQUIERDA */}
            <div className="flex flex-col h-full">
              {/* Imágenes */}
              <div 
                className="relative flex items-center justify-center flex-1"
                onMouseEnter={() => setShowThumbnails(true)}
                onMouseLeave={() => setShowThumbnails(false)}
              >
                {/* Imagen Principal */}
                <div 
                  className="bg-white rounded-xl shadow-2xl overflow-hidden cursor-pointer relative group"
                  style={{ width: '90%', height: '90%' }}
                  onClick={() => !images[currentImageIndex] && triggerFileInput(currentImageIndex)}
                >
                  {!imagesLoaded ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                    </div>
                  ) : images[currentImageIndex] ? (
                    <>
                      <OptimizedImage 
                        src={images[currentImageIndex]} 
                        alt="Producto" 
                        className="w-full h-full"
                        objectFit="cover"
                      />
                      {/* Botones de acción sobre la imagen */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); triggerFileInput(currentImageIndex); }}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 shadow-lg"
                          title="Cambiar imagen"
                        >
                          <RefreshCw size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setFullscreenImage(true); }}
                          className="p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 shadow-lg"
                          title="Ampliar imagen"
                        >
                          <Maximize2 size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); eliminarImagen(currentImageIndex); }}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 shadow-lg"
                          title="Eliminar imagen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  ) : (
                    placeholderSVG
                  )}
                </div>

                {/* Miniaturas */}
                <div 
                  className={`grid grid-cols-3 gap-3 absolute bottom-4 left-1/2 transform -translate-x-1/2 w-4/5 max-w-md transition-opacity duration-300 ${
                    showThumbnails ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {images.map((img, index) => (
                    <div
                      key={index}
                      className={`aspect-square bg-white rounded-lg overflow-hidden shadow-lg cursor-pointer transition-all duration-300 relative group ${
                        index === currentImageIndex 
                          ? 'ring-4 ring-purple-600 shadow-purple-400/40 shadow-xl' 
                          : 'hover:ring-2 hover:ring-purple-400 hover:scale-105'
                      }`}
                      onClick={() => changeImage(index)}
                    >
                      {img ? (
                        <>
                          <OptimizedImage 
                            src={img} 
                            alt={`Miniatura ${index + 1}`} 
                            className="w-full h-full"
                            objectFit="cover"
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); eliminarImagen(index); }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Eliminar"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full scale-75">{placeholderSVG}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* COLUMNA DERECHA */}
            <div className="flex flex-col gap-3 overflow-y-auto pr-2">
              
              {/* CÓDIGO - Fondo café, letra blanca */}
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#8B4513' }}>
                <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">Código</span>
                <p className="text-2xl font-bold text-white mt-1">
                  {productoActual?.codigo || 'N/A'}
                </p>
              </div>

              {/* NOMBRE */}
              <div className="bg-white rounded-xl p-5 shadow-lg">
                <h1 className="text-3xl font-bold text-slate-900 text-center leading-tight">
                  {limpiarNombre(productoActual?.nombre)}
                </h1>
              </div>

              {/* CANTIDAD POR CAJA */}
              {cantidadPorCaja && (
                <div className="bg-amber-50 rounded-xl p-4 shadow-md border-2 border-amber-200">
                  <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3 text-center">
                    📦 Cantidad por Caja
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <div className="text-xs text-amber-600 font-medium mb-1">Metros Cuadrados</div>
                      <div className="text-2xl font-bold text-amber-800">
                        {cantidadPorCaja.metrosCuadrados} m²
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <div className="text-xs text-amber-600 font-medium mb-1">Unidades</div>
                      <div className="text-2xl font-bold text-amber-800">
                        {cantidadPorCaja.unidades} U
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* REFERENCIA */}
              <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-200">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  📖 Referencia
                </div>
                <div className="text-xl font-bold text-slate-900">
                  {productoActual?.referencia || 'N/A'}
                </div>
              </div>

              {/* STOCK POR BODEGA */}
              <div className="bg-white rounded-xl p-4 shadow-md flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  🏪 Cantidad de Materiales Disponibles por Bodega
                </div>
                {productoActual?.warehouses && productoActual.warehouses.length > 0 ? (
                  <div className="space-y-3">
                    {productoActual.warehouses.map((w, idx) => (
                      <div 
                        key={idx} 
                        className={`flex justify-between items-center p-3 rounded-lg ${
                          w.quantity <= 0 
                            ? 'bg-red-50 border border-red-200' 
                            : 'bg-green-50 border border-green-200'
                        }`}
                      >
                        <span className="font-medium text-gray-700">
                          {w.name || `Bodega ${idx + 1}`}
                        </span>
                        <span className={`text-xl font-bold ${
                          w.quantity <= 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {w.quantity || 0} unidades
                        </span>
                      </div>
                    ))}
                    
                    <div className="flex justify-between items-center p-3 rounded-lg bg-purple-50 border-2 border-purple-300 mt-2">
                      <span className="font-bold text-purple-700">TOTAL DISPONIBLE</span>
                      <span className="text-2xl font-bold text-purple-700">
                        {productoActual?.cantidad || 0} unidades
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>Sin información de bodegas</p>
                    <p className="text-lg font-semibold mt-2">
                      Stock total: {productoActual?.cantidad || 0} unidades
                    </p>
                  </div>
                )}
              </div>

              {/* Indicador */}
              <div className="bg-purple-50 rounded-lg p-3 text-center mt-auto">
                <span className="text-purple-700 font-medium">
                  Producto {currentIndex + 1} de {productosFiltrados.length}
                </span>
              </div>
            </div>
          </div>

          {/* Flecha derecha */}
          <button
            onClick={irSiguiente}
            disabled={currentIndex === productosFiltrados.length - 1}
            className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-all ml-4 ${
              currentIndex === productosFiltrados.length - 1
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                : 'bg-white shadow-lg text-purple-600 hover:bg-purple-50 hover:scale-110'
            }`}
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}

      {/* Modal de imagen a pantalla completa */}
      {fullscreenImage && images[currentImageIndex] && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setFullscreenImage(false)}
        >
          {/* Botón cerrar */}
          <button
            onClick={() => setFullscreenImage(false)}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
            title="Cerrar"
          >
            <X size={28} />
          </button>

          {/* Navegación izquierda */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (currentImageIndex > 0) {
                setCurrentImageIndex(currentImageIndex - 1);
              } else if (images[2]) {
                setCurrentImageIndex(2);
              } else if (images[1]) {
                setCurrentImageIndex(1);
              }
            }}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <ChevronLeft size={32} />
          </button>

          {/* Imagen ampliada */}
          <OptimizedImage 
            src={images[currentImageIndex]} 
            alt="Producto ampliado" 
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
            objectFit="contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Navegación derecha */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (currentImageIndex < 2 && images[currentImageIndex + 1]) {
                setCurrentImageIndex(currentImageIndex + 1);
              } else if (images[0] && currentImageIndex !== 0) {
                setCurrentImageIndex(0);
              }
            }}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <ChevronRight size={32} />
          </button>

          {/* Indicadores de imagen */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3">
            {images.map((img, index) => (
              img && (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentImageIndex 
                      ? 'bg-white scale-125' 
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              )
            ))}
          </div>

          {/* Información del producto */}
          <div className="absolute bottom-6 left-6 text-white">
            <p className="text-sm text-white/60">Imagen {currentImageIndex + 1} de {images.filter(i => i).length}</p>
          </div>
        </div>
      )}
    </div>
  );
}
